// TierGate — route wrapper that enforces auth + subscription tier.
// Lives around every protected route via an outlet wrapper in App.tsx.
//
// Two-stage gate:
//   1. Auth — if no session, redirect to /auth (preserving attempted
//      path via location.state so post-login can return them).
//   2. Tier — if session exists, compare user's tier to the route's
//      required tier. If below, redirect to /upgrade (again preserving
//      attempted path so the upgrade CTA can contextualize).
//
// Special cases:
//   * /upgrade is always reachable for any authenticated user — it's
//     the destination for above-tier traffic, so it can never gate
//     against itself.
//   * Free-tier users (has account, no active subscription) bounce to
//     /upgrade on every route except /upgrade and /settings.

import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { canAccessTier, tierForPath } from "@/data/tierAccess";

const ALWAYS_REACHABLE_FOR_AUTHENTICATED = new Set<string>([
  "/upgrade",
  "/pricing",
  "/checkout/success",
  "/checkout/cancel",
  "/settings",
]);

export function TierGate() {
  const location = useLocation();
  const { session, loading: authLoading } = useUserProfile();
  const { tier, loading: tierLoading } = useSubscriptionTier();

  const isFreeTier = !!session && tier === "free";

  /**
   * ⚠️ A PLATFORM ADMIN IS NEVER BOUNCED, AND THIS GATE HAD NO IDEA.
   *
   * Found 2026-09-13, live, on the owner's own account: "whenever I click on
   * any tool it takes me to my barn." He is a platform `admin` AND a `manager`
   * at three barns, and has no `profiles` row — and `useSubscriptionTier`
   * resolves a missing row to "free" (not "select"; CLAUDE.md says otherwise
   * and is stale). So every gated route hit `tier === "free"` +
   * `isBarnMember` and redirected him to /my-barn.
   *
   * BarnOnlyLock had already learned this lesson and exempts admins
   * (`if (i.isAdmin) return false`). This gate never checked.
   *
   * The `resolved` flag is load-bearing and copied from BarnOnlyLock's own
   * hard-won rule: NEVER act on a merely-falsy `isAdmin`, because it is false
   * while it is still loading and acting on it redirects the admin before the
   * answer arrives — which is the exact bug that guard carries a comment about.
   */
  const { isAdmin, resolved: adminResolved } = useIsAdmin();

  // Hold the render while we figure out auth + tier (+ barn membership for free
  // users). Without this the guard would flash the target page then redirect,
  // producing a jarring double-paint.
  if (authLoading || (session && tierLoading) || (isFreeTier && !adminResolved)) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Stage 1 — auth. No session → bounce to /auth and remember where
  // they came from so we can resume after login.
  if (!session) {
    return (
      <Navigate
        to="/auth"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Stage 2 — tier. /upgrade + /settings are always reachable so the
  // user can resolve their subscription or manage their profile even
  // when locked out of tools.
  if (ALWAYS_REACHABLE_FOR_AUTHENTICATED.has(location.pathname)) {
    return <Outlet />;
  }

  const required = tierForPath(location.pathname);

  // Free tier = has account but no subscription.
  if (tier === "free") {
    // ⚠️ THE BARN CASE IS GONE (owner, 2026-09-13): "I don't want my barn
    // attached to this website login."
    //
    // This used to divert a barn member to /my-barn instead of the paywall.
    // Together with BarnOnlyLock — removed in the same change — it meant a barn
    // session left open in a browser hijacked every navigation on the platform.
    // That is not hypothetical: a Stockmen's login from a week earlier made
    // every tool bounce to a barn dashboard belonging to someone else, and it
    // took an hour to tell apart from a bug in the platform itself.
    //
    // A barn person reading Market Intelligence is now simply a person without
    // a subscription, and sees what any of them sees. The two products meet at
    // no point in this file.

    // An admin runs this product. Whatever else they are — a barn manager, a
    // user with no subscription row — they are never sent away from it.
    if (isAdmin) {
      return <Outlet />;
    }
    // Otherwise bounce to /pricing so they can pick a plan and start checkout.
    return (
      <Navigate
        to="/pricing"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  if (!tier || !canAccessTier(tier, required)) {
    // Existing subscriber trying to reach an above-tier tool → /upgrade
    // (the contextual "upgrade to Choice" prompt). Different surface
    // from /pricing which is for non-paying users choosing a plan.
    return (
      <Navigate
        to="/upgrade"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  return <Outlet />;
}
